/**
 * FAQ page interactive behaviour.
 * Handles: search (debounced), category filter tabs with Swiper carousel, scroll-to-top.
 *
 * Works with a shell <faq-main> plus sibling page-faq-group sections.
 * Groups are discovered page-wide via [data-faq-group]; filter tabs are
 * built from each group's data-faq-filter-label when present.
 */

import { createSwiper } from '@nulo/swiper';

const DEBOUNCE_MS = 400;

/**
 * @extends {HTMLElement}
 */
class FaqMain extends HTMLElement {
  /** @type {import('swiper').Swiper | null} */
  #swiper = null;

  /** @type {AbortController|null} */
  #abort = null;

  /** @type {ReturnType<typeof setTimeout>|null} */
  #debounceTimer = null;

  /** @type {string} */
  #activeFilter = 'ALL';

  /** @type {string} */
  #searchQuery = '';

  /** @type {string[]} */
  #tags = ['ALL'];

  connectedCallback() {
    if (this._initialized) return;
    this._initialized = true;

    this.#abort = new AbortController();
    const { signal } = this.#abort;

    this.#buildFilterTabs();

    // Module scripts run after full parse; rAF covers Theme Editor late mounts.
    this.#hydrateFaqPageSchema();
    requestAnimationFrame(() => this.#hydrateFaqPageSchema());

    const input = this.querySelector('[data-faq-search]');
    const clearBtn = this.querySelector('[data-faq-clear]');

    if (input) {
      input.addEventListener('input', () => {
        this.#scheduleSearch(input.value);
        if (clearBtn) clearBtn.setAttribute('aria-hidden', input.value ? 'false' : 'true');
      }, { signal });

      const form = input.closest('form');
      if (form) {
        form.addEventListener('submit', (e) => {
          e.preventDefault();
          clearTimeout(this.#debounceTimer);
          this.#searchQuery = input.value.trim().toLowerCase();
          this.#applyFilters();
        }, { signal });
      }
    }

    if (clearBtn) {
      clearBtn.addEventListener('click', () => {
        if (input) {
          input.value = '';
          input.focus();
        }
        clearBtn.setAttribute('aria-hidden', 'true');
        clearTimeout(this.#debounceTimer);
        this.#searchQuery = '';
        this.#applyFilters();
      }, { signal });
    }

    this.#bindFilterClicks(signal);
    this.#bindScrollTop(signal);

    window.addEventListener('resize', () => {
      if (window.matchMedia('(min-width: 992px)').matches) {
        this.#destroySwiper();
      } else if (!this.#swiper) {
        this.#initSwiper();
      }
    }, { signal, passive: true });

    this.#applyFilters();
  }

  disconnectedCallback() {
    this.#destroySwiper();
    this.#abort?.abort();
    this.#abort = null;
    clearTimeout(this.#debounceTimer);
    this.#debounceTimer = null;
    this._initialized = false;
  }

  #initSwiper() {
    this.#destroySwiper();

    if (window.matchMedia('(min-width: 992px)').matches) return;

    const mobileContainer = this.querySelector('[data-faq-filters-mobile]');
    const swiperEl = this.querySelector('[data-faq-swiper]');
    if (!mobileContainer || !swiperEl) return;

    const slidesCount = swiperEl.querySelectorAll('.swiper-slide').length;
    if (slidesCount <= 1) return;

    this.#swiper = createSwiper({
      region: mobileContainer,
      el: swiperEl,
      activeSlideClass: 'swiper-slide-active',
      config: {
        slidesPerView: 'auto',
        spaceBetween: 10,
        watchOverflow: true,
        speed: 500,
        navigation: {
          nextEl: this.querySelector('[data-faq-next]'),
          prevEl: this.querySelector('[data-faq-prev]'),
        },
      },
    });
  }

  #destroySwiper() {
    this.#swiper?.destroy?.(true, true);
    this.#swiper = null;
  }

  /**
   * Build a single FAQPage JSON-LD from every category group's Question
   * entities. Replaces any previous FAQPage schema this component created.
   */
  #hydrateFaqPageSchema() {
    /** @type {object[]} */
    const mainEntity = [];

    for (const chunk of document.querySelectorAll('[data-faq-schema-entities]')) {
      try {
        const parsed = JSON.parse(chunk.textContent || '[]');
        if (!Array.isArray(parsed)) continue;
        for (const item of parsed) {
          if (item?.['@type'] === 'Question' && item.name) {
            mainEntity.push(item);
          }
        }
      } catch {
        // Ignore malformed category chunks.
      }
    }

    const existing = document.querySelector('script[data-faq-page-schema]');
    if (!mainEntity.length) {
      existing?.remove();
      return;
    }

    const pageUrl = this.dataset.faqSchemaUrl || window.location.href.split('?')[0];
    const pageId = this.dataset.faqSchemaId || `${pageUrl}#faq`;
    const schema = {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      '@id': pageId,
      url: pageUrl,
      mainEntity,
    };

    const script = existing || document.createElement('script');
    script.type = 'application/ld+json';
    script.setAttribute('data-faq-page-schema', '');
    script.textContent = JSON.stringify(schema);
    if (!existing) {
      document.head.appendChild(script);
    }
  }

  /** Collect FAQ group elements (local first, then page-wide siblings). */
  #getGroups() {
    const local = this.querySelectorAll('[data-faq-group]');
    if (local.length) return local;
    return document.querySelectorAll('[data-faq-group]');
  }

  /**
   * Build category filter buttons from group sections.
   * Tabs mirror FAQ category section order on the template.
   */
  #buildFilterTabs() {
    const desktopContainer = this.querySelector('[data-faq-filters-desktop]');
    const mobileWrapper = this.querySelector('[data-faq-filters-mobile-wrapper]');
    const mobileContainer = this.querySelector('[data-faq-filters-mobile]');

    if (desktopContainer) {
      for (const btn of desktopContainer.querySelectorAll('[data-faq-auto]')) {
        btn.remove();
      }
    }
    if (mobileWrapper) {
      for (const slide of mobileWrapper.querySelectorAll('[data-faq-auto]')) {
        slide.remove();
      }
    }

    const seen = new Set();
    const tags = ['ALL'];

    for (const group of this.#getGroups()) {
      const tag = group.dataset.faqGroup?.trim();
      if (!tag || seen.has(tag)) continue;
      seen.add(tag);
      tags.push(tag);

      const label = group.dataset.faqFilterLabel?.trim() || tag;

      if (desktopContainer) {
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'faq-page__filter-btn';
        btn.dataset.faqFilter = tag;
        btn.dataset.faqAuto = 'true';
        btn.setAttribute('aria-pressed', 'false');
        btn.textContent = label;
        desktopContainer.appendChild(btn);
      }

      if (mobileWrapper) {
        const slide = document.createElement('div');
        slide.className = 'swiper-slide !w-[110px]';
        slide.dataset.faqAuto = 'true';

        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'faq-page__filter-btn';
        btn.dataset.faqFilter = tag;
        btn.setAttribute('aria-pressed', 'false');
        btn.textContent = label;

        slide.appendChild(btn);
        mobileWrapper.appendChild(slide);
      }
    }

    this.#tags = tags;

    const hasFilters = seen.size > 0;
    if (desktopContainer) desktopContainer.hidden = !hasFilters;
    if (mobileContainer) mobileContainer.hidden = !hasFilters;

    this.#initSwiper();
    this.#updateTabState();
  }

  /** @param {AbortSignal} signal */
  #bindFilterClicks(signal) {
    const onFilterClick = (event) => {
      const btn = event.target.closest('[data-faq-filter]');
      if (!btn || !this.contains(btn)) return;

      this.#activeFilter = btn.dataset.faqFilter || 'ALL';
      this.#updateTabState();

      const input = this.querySelector('[data-faq-search]');
      const clearBtn = this.querySelector('[data-faq-clear]');
      if (input) {
        input.value = '';
        if (clearBtn) clearBtn.setAttribute('aria-hidden', 'true');
      }
      clearTimeout(this.#debounceTimer);
      this.#searchQuery = '';
      this.#applyFilters();
    };

    const desktopContainer = this.querySelector('[data-faq-filters-desktop]');
    const mobileContainer = this.querySelector('[data-faq-filters-mobile]');

    desktopContainer?.addEventListener('click', onFilterClick, { signal });
    mobileContainer?.addEventListener('click', onFilterClick, { signal });
  }

  /** @param {string} value */
  #scheduleSearch(value) {
    clearTimeout(this.#debounceTimer);
    this.#debounceTimer = setTimeout(() => {
      this.#searchQuery = value.trim().toLowerCase();
      this.#activeFilter = 'ALL';
      this.#updateTabState();
      this.#applyFilters();
    }, DEBOUNCE_MS);
  }

  /** @param {AbortSignal} signal */
  #bindScrollTop(signal) {
    const scrollBtn = this.querySelector('[data-faq-scroll-top]');
    if (!scrollBtn) return;

    const onScroll = () => {
      const show = window.scrollY > 400;
      scrollBtn.setAttribute('aria-hidden', show ? 'false' : 'true');
      if (show) this.#updateScrollTopPosition();
    };

    window.addEventListener('scroll', onScroll, { signal, passive: true });
    window.addEventListener('resize', () => this.#updateScrollTopPosition(), { signal, passive: true });
    window.addEventListener('nulo:hfc-footer-near', () => this.#updateScrollTopPosition(), { signal });
    window.addEventListener('nulo:sticky-cta-state', () => this.#updateScrollTopPosition(), { signal });

    onScroll();

    scrollBtn.addEventListener('click', () => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }, { signal });

    this.#observeHfcFrame(signal);
  }

  /** @param {AbortSignal} signal */
  #observeHfcFrame(signal) {
    const update = () => this.#updateScrollTopPosition();

    /** @param {Element|null} frame */
    const attachFrameObserver = (frame) => {
      if (!frame || frame.__nuloScrollObserverBound) return;
      frame.__nuloScrollObserverBound = true;
      const observer = new MutationObserver(update);
      observer.observe(frame, { attributes: true, attributeFilter: ['style', 'class'] });
      signal.addEventListener('abort', () => observer.disconnect(), { once: true });
      update();
    };

    const existingFrame = document.querySelector('#hfc-frame');
    if (existingFrame) {
      attachFrameObserver(existingFrame);
    } else {
      const bodyObserver = new MutationObserver(() => {
        const frame = document.querySelector('#hfc-frame');
        if (frame) {
          attachFrameObserver(frame);
          bodyObserver.disconnect();
        }
      });
      bodyObserver.observe(document.body, { childList: true, subtree: true });
      signal.addEventListener('abort', () => bodyObserver.disconnect(), { once: true });
    }
  }

  #updateScrollTopPosition() {
    const scrollBtn = this.querySelector('[data-faq-scroll-top]');
    if (!scrollBtn || scrollBtn.getAttribute('aria-hidden') === 'true') return;

    const hfcFrame = document.querySelector('#hfc-frame');
    let baseBottom = 92;

    if (hfcFrame && hfcFrame.offsetWidth > 0 && hfcFrame.offsetHeight > 0) {
      const rect = hfcFrame.getBoundingClientRect();
      const viewportHeight = window.innerHeight;

      if (rect.height > 0 && rect.height < 250 && rect.top < viewportHeight && rect.bottom > 0) {
        const hfcTopFromViewportBottom = viewportHeight - rect.top;
        baseBottom = Math.max(92, Math.round(hfcTopFromViewportBottom + 12));
      }
    }

    scrollBtn.style.bottom = `${baseBottom}px`;
  }

  #updateTabState() {
    for (const btn of this.querySelectorAll('[data-faq-filter]')) {
      btn.setAttribute('aria-pressed', btn.dataset.faqFilter === this.#activeFilter ? 'true' : 'false');
    }

    const activeIndex = this.#tags.indexOf(this.#activeFilter);
    if (this.#swiper && activeIndex >= 0) {
      this.#swiper.slideTo(activeIndex);
    }
  }

  /**
   * Hide/show the group and its owning section (padding lives on the section shell).
   * @param {HTMLElement} group
   * @param {boolean} visible
   */
  #setGroupVisible(group, visible) {
    group.hidden = !visible;
    const section = group.closest('.shopify-section') || group.closest('.section-wrapper');
    if (section) section.hidden = !visible;
  }

  #applyFilters() {
    const groups = this.#getGroups();
    let totalVisible = 0;

    for (const group of groups) {
      const groupTag = group.dataset.faqGroup || '';
      const inFilter = this.#activeFilter === 'ALL' || groupTag === this.#activeFilter;

      if (!inFilter) {
        this.#setGroupVisible(group, false);
        continue;
      }

      let groupVisible = 0;
      for (const item of group.querySelectorAll('[data-faq-item]')) {
        const haystack = item.dataset.faqHaystack || '';
        const matchesSearch = !this.#searchQuery || haystack.includes(this.#searchQuery);
        item.hidden = !matchesSearch;
        if (matchesSearch) groupVisible++;
      }

      this.#setGroupVisible(group, groupVisible > 0);
      totalVisible += groupVisible;
    }

    const status = this.querySelector('[data-faq-status]');
    if (status) {
      const one = this.dataset.faqResultsOne || '{{ count }} result found';
      const other = this.dataset.faqResultsOther || '{{ count }} results found';
      const template = totalVisible === 1 ? one : other;
      status.textContent = template.replace('{{ count }}', String(totalVisible));
    }

    const noResults = this.querySelector('[data-faq-no-results]');
    if (noResults) {
      noResults.hidden = totalVisible > 0;
    }
  }
}

if (!customElements.get('faq-main')) {
  customElements.define('faq-main', FaqMain);
}
